import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';

@customElement('bia-chat-input')
export class BiaChatInput extends LitElement {
  static styles = css`
    :host { display: block; }
    .wrap {
      position: relative;
      padding: 14px 16px 18px;
      background: var(--bia-background, #fafaf7);
      border-top: 1px solid var(--bia-border, #e8e5dc);
    }
    .field {
      position: relative;
      display: flex;
      align-items: flex-end;
      gap: 8px;
      padding: 10px 12px 10px 16px;
      background: var(--bia-input-bg, #fff);
      border: 1px solid var(--bia-input-border, #e8e5dc);
      border-radius: 14px;
      transition: border-color 160ms ease, box-shadow 160ms ease;
    }
    .field:focus-within {
      border-color: var(--bia-primary, #1a1a2e);
      box-shadow: 0 0 0 3px rgba(26, 26, 46, 0.06);
    }
    textarea {
      flex: 1;
      resize: none;
      border: none;
      outline: none;
      background: transparent;
      padding: 4px 0;
      font-family: var(--bia-font-family, sans-serif);
      font-size: 14px;
      line-height: 1.5;
      color: var(--bia-text, #0f0f0e);
      max-height: 120px;
      min-height: 22px;
    }
    textarea::placeholder {
      color: var(--bia-text-muted, #6b6b66);
      opacity: 0.7;
    }
    textarea:disabled {
      background: transparent;
      cursor: not-allowed;
      opacity: 0.5;
    }
    button.send {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--bia-primary, #1a1a2e);
      color: var(--bia-primary-text, #fafaf7);
      border: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
    }
    button.send:hover:not(:disabled) {
      transform: translateY(-1px) scale(1.04);
    }
    button.send:active:not(:disabled) {
      transform: translateY(0) scale(0.98);
    }
    button.send:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
    button.send svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .hint {
      margin-top: 8px;
      font-family: var(--bia-font-family, sans-serif);
      font-size: 10px;
      color: var(--bia-text-muted, #6b6b66);
      letter-spacing: 0.04em;
      text-align: center;
      opacity: 0.6;
    }
    kbd {
      font-family: inherit;
      background: var(--bia-hover-bg, rgba(15, 15, 14, 0.04));
      border-radius: 3px;
      padding: 1px 4px;
      font-size: 9px;
    }
  `;

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: String }) placeholder = 'Pergunte sobre seus dados...';
  @property({ type: String }) prefill = '';

  @state() private value = '';
  @query('textarea') private textarea!: HTMLTextAreaElement;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('prefill') && this.prefill) {
      this.value = this.prefill;
      if (this.textarea) {
        this.textarea.value = this.prefill;
        // Resize to fit the prefilled content (shared with onInput
        // path so both sources of value changes produce identical sizing)
        this.resizeTextarea(this.textarea);
        // Focus so the user can hit Enter or start editing immediately
        this.textarea.focus();
        // Place cursor at end of text instead of selecting everything
        const len = this.textarea.value.length;
        this.textarea.setSelectionRange(len, len);
      }
    }
  }

  /** Auto-resize the textarea up to a fixed maximum height. Called from
   * both the input handler (every keystroke) and the prefill updater
   * (when a programmatic value lands), so both paths produce identical
   * sizing without drift. */
  private resizeTextarea(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }

  private onInput(event: Event): void {
    this.value = (event.target as HTMLTextAreaElement).value;
    this.resizeTextarea(event.target as HTMLTextAreaElement);
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  private send(): void {
    if (this.disabled) return;
    const trimmed = this.value.trim();
    if (!trimmed) return;
    this.dispatchEvent(
      new CustomEvent<string>('send', { detail: trimmed, bubbles: true, composed: true }),
    );
    this.value = '';
    if (this.textarea) {
      this.textarea.value = '';
      this.textarea.style.height = 'auto';
    }
  }

  render() {
    const isEmpty = this.value.trim().length === 0;
    return html`
      <div class="wrap">
        <div class="field">
          <textarea
            rows="1"
            .value=${this.value}
            ?disabled=${this.disabled}
            placeholder=${this.placeholder}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
          ></textarea>
          <button
            class="send"
            ?disabled=${this.disabled || isEmpty}
            @click=${this.send}
            aria-label="Enviar mensagem"
            title="Enviar (Enter)"
          >
            <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
        <div class="hint"><kbd>Enter</kbd> envia · <kbd>Shift+Enter</kbd> nova linha</div>
      </div>
    `;
  }
}
